// src/Components/GoogleMap/CameraAnimator.js

/**
 * Easing function: Quartic (t^4)
 * Provides a very slow, smooth start and an ultra-gentle landing.
 * Perfect for slower, cinematic camera movements.
 */
const easeInOutQuart = (t) => {
  return t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;
};

let currentAnimationId = null;

export const smoothFlyTo = (map, targetLat, targetLng, targetZoom) => {
  // 1. Clear existing animation
  if (currentAnimationId) {
    cancelAnimationFrame(currentAnimationId);
  }

  const startZoom = map.getZoom();
  const startCenter = map.getCenter();
  const startLat = startCenter.lat();
  const startLng = startCenter.lng();

  // AMENDED: Calculate the absolute shortest path for longitude (Anti-Meridian crossing)
  let deltaLng = targetLng - startLng;
  // Normalize delta to be between -180 and +180 degrees
  while (deltaLng > 180) deltaLng -= 360;
  while (deltaLng < -180) deltaLng += 360;

  // This is the physical destination on the continuous map (avoids the "Primary Earth" snap)
  const continuousTargetLng = startLng + deltaLng;

  // Calculate Distance (using the corrected shortest-path longitude)
  const dist = Math.sqrt(
    Math.pow(targetLat - startLat, 2) + Math.pow(continuousTargetLng - startLng, 2),
  );

  // Requirement: Check if we are already there
  if (dist < 0.0001 && Math.abs(startZoom - targetZoom) < 0.1) {
    return;
  }

  // 2. CINEMATIC FLIGHT CONFIGURATION
  // Base duration increased to 2000ms (2s)
  // Distance multiplier increased to 600ms
  // Max cap increased to 5500ms (5.5 seconds) for ultimate smoothness
  const duration = Math.min(2000 + dist * 600, 5500);

  // Refined Apex (Altitude):
  let apexZoom;
  if (dist > 15.0) {
    apexZoom = 5.5;
  } else if (dist > 5.0) {
    apexZoom = 8.0;
  } else if (dist > 1.0) {
    apexZoom = 11.0;
  } else {
    apexZoom = Math.min(startZoom, targetZoom) - 0.8;
  }

  const safeApex = Math.min(startZoom, targetZoom, apexZoom);

  // 3. Start Animation
  const startTime = performance.now();

  const animate = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    const t = easeInOutQuart(progress);

    // A. Interpolate Position
    const lat = startLat + (targetLat - startLat) * t;
    // AMENDED: Use the continuous target longitude so we don't snap across the date line
    const lng = startLng + (continuousTargetLng - startLng) * t;

    // B. Interpolate Zoom (The Glide Arc)
    let currentZoom;
    if (progress <= 0.5) {
      // Phase 1: Slow Climb
      const subT = progress * 2;
      currentZoom = startZoom + (safeApex - startZoom) * easeInOutQuart(subT);
    } else {
      // Phase 2: Gentle Descent
      const subT = (progress - 0.5) * 2;
      currentZoom = safeApex + (targetZoom - safeApex) * easeInOutQuart(subT);
    }

    // C. Atomic Move
    map.moveCamera({
      center: { lat, lng },
      zoom: currentZoom,
    });

    if (progress < 1) {
      currentAnimationId = requestAnimationFrame(animate);
    } else {
      currentAnimationId = null;
      // AMENDED: Soft landing. Use the continuous longitude instead of the raw targetLng.
      // This forces Google Maps to stay on the continuous tile it just flew to.
      map.moveCamera({
        center: { lat: targetLat, lng: continuousTargetLng },
        zoom: targetZoom,
      });
    }
  };

  currentAnimationId = requestAnimationFrame(animate);
};
